import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Loader2, Search, Plus, LogOut, Trash2, X, Shield, Calendar, Clock, Upload, Eye } from "lucide-react";
import { clientsApi, clientsToken, clientsLogout } from "@/lib/clientsApi";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const AuthImage = ({ path, className, onClick }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let active = true;
    let url;
    clientsApi
      .get("/clients/file", { params: { path }, responseType: "blob" })
      .then((r) => {
        if (active) {
          url = URL.createObjectURL(r.data);
          setSrc(url);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);
  if (!src) return <div className={`${className} bg-white/5 animate-pulse`} />;
  return <img src={src} alt="sənəd" className={className} onClick={onClick} loading="lazy" />;
};

const nowParts = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

function Gate({ onOk }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await clientsApi.post("/clients/auth", { password: pw });
      localStorage.setItem("clients_token", data.token);
      onOk();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Xəta baş verdi");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-[#0a0a0f]">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm rounded-3xl p-8 bg-white/5 border border-white/10"
        data-testid="clients-gate-form"
      >
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#2997FF] to-[#0a84ff] flex items-center justify-center mb-5">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white">Alış Jurnalı</h1>
        <p className="text-slate-400 text-sm mb-6">Girişi qorunan bölmə</p>
        <label className="text-sm text-slate-300 mb-1.5 block">Şifrə</label>
        <Input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="••••••••"
          data-testid="clients-password-input"
          className="bg-white/5 border-white/10 h-11 text-white"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="clients-login-btn"
          className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-black font-medium disabled:opacity-50 hover:bg-slate-200 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Daxil ol
        </button>
      </motion.form>
    </div>
  );
}

const emptyForm = () => ({
  seller_first_name: "",
  seller_last_name: "",
  seller_phone: "",
  imei: "",
  phone_model: "",
  passport_number: "",
  fin: "",
  note: "",
  passport_front: null,
  passport_back: null,
  document_photo: null,
});

function AddModal({ onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [today, setToday] = useState(true);
  const [dt, setDt] = useState(nowParts());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  const upload = async (file, side) => {
    if (!file) return;
    setUploading(side);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await clientsApi.post("/clients/upload", fd);
      setForm((f) => ({ ...f, [side]: data.path }));
    } catch (err) {
      toast.error("Şəkil yüklənmədi");
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    if (!form.seller_first_name || !form.seller_last_name || !form.imei || !form.phone_model) {
      toast.error("Ad, soyad, IMEI və model mütləqdir");
      return;
    }
    const parts = today ? nowParts() : dt;
    setSaving(true);
    try {
      await clientsApi.post("/clients/purchases", {
        ...form,
        purchase_at: `${parts.date}T${parts.time}`,
      });
      toast.success("Əlavə edildi");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Xəta");
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, placeholder) => (
    <div>
      <label className="text-xs text-slate-400 mb-1 block">{label}</label>
      <Input
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        data-testid={`add-${key}`}
        className="bg-white/5 border-white/10 h-11 text-white"
      />
    </div>
  );

  const photoBox = (label, side) => (
    <div>
      <label className="text-xs text-slate-400 mb-1 block">{label}</label>
      <label
        data-testid={`add-${side}-label`}
        className="flex items-center justify-center gap-2 h-24 rounded-xl border border-dashed border-white/15 bg-white/5 cursor-pointer text-slate-400 text-sm overflow-hidden"
      >
        {form[side] ? (
          <AuthImage path={form[side]} className="w-full h-full object-cover" />
        ) : uploading === side ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Upload className="w-4 h-4" /> Şəkil seç
          </>
        )}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`add-${side}-input`}
          onChange={(e) => upload(e.target.files?.[0], side)}
        />
      </label>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg rounded-2xl bg-[#12121a] border border-white/10 p-6 my-8"
        data-testid="add-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-white">Yeni alış</h2>
          <button onClick={onClose} data-testid="add-close" className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {field("Ad", "seller_first_name", "Ad")}
          {field("Soyad", "seller_last_name", "Soyad")}
          {field("Mobil nömrə", "seller_phone", "+994 50 123 45 67")}
          {field("IMEI", "imei", "IMEI kodu")}
          {field("Model", "phone_model", "iPhone 13 Pro")}
          {field("Pasport", "passport_number", "AA1234567")}
          {field("FIN", "fin", "1234567")}
        </div>
        <div className="mt-3">
          <label className="text-xs text-slate-400 mb-1 block">Qeyd (istəyə görə)</label>
          <Input
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="Əlavə qeyd"
            data-testid="add-note"
            className="bg-white/5 border-white/10 h-11 text-white"
          />
        </div>

        <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-4">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-white">
            <input
              type="checkbox"
              checked={today}
              onChange={(e) => setToday(e.target.checked)}
              data-testid="add-today-toggle"
              className="w-4 h-4 accent-[#2997FF]"
            />
            Bu gün alınıb?
          </label>
          {!today && (
            <div className="grid grid-cols-2 gap-3 mt-3" data-testid="add-datetime">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Tarix</label>
                <Input
                  type="date"
                  value={dt.date}
                  onChange={(e) => setDt((d) => ({ ...d, date: e.target.value }))}
                  data-testid="add-date"
                  className="bg-white/5 border-white/10 h-11 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Təxmini saat</label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="13:00"
                  list="time-options"
                  value={dt.time}
                  onChange={(e) => setDt((d) => ({ ...d, time: e.target.value }))}
                  data-testid="add-time"
                  className="bg-white/5 border-white/10 h-11 text-white"
                />
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          {photoBox("Pasport (ön)", "passport_front")}
          {photoBox("Pasport (arxa)", "passport_back")}
          {photoBox("Sənəd (imzalı)", "document_photo")}
        </div>

        <button
          onClick={save}
          disabled={saving}
          data-testid="add-save"
          className="w-full mt-5 flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#2997FF] text-white font-medium disabled:opacity-50 hover:bg-[#0a84ff] transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Yadda saxla
        </button>
      </motion.div>
    </div>
  );
}

function DetailModal({ item, onClose, onDeleted }) {
  const del = async () => {
    if (!window.confirm("Bu qeydi silmək istədiyinizə əminsiniz?")) return;
    try {
      await clientsApi.delete(`/clients/purchases/${item.id}`);
      toast.success("Silindi");
      onDeleted();
    } catch {
      toast.error("Silinmədi");
    }
  };
  const [t, d] = [item.purchase_at?.split("T")[1]?.slice(0, 5), item.purchase_date];
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg rounded-2xl bg-[#12121a] border border-white/10 p-6 my-8"
        data-testid="detail-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">
            {item.seller_first_name} {item.seller_last_name}
          </h2>
          <button onClick={onClose} data-testid="detail-close" className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-2 text-sm">
          <Row label="IMEI" value={item.imei} />
          <Row label="Model" value={item.phone_model} />
          <Row label="Mobil" value={item.seller_phone || "—"} />
          <Row label="Pasport" value={item.passport_number || "—"} />
          <Row label="FIN" value={item.fin || "—"} />
          <Row label="Tarix" value={d} />
          <Row label="Saat" value={t} />
          {item.note ? <Row label="Qeyd" value={item.note} /> : null}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          {item.passport_front && (
            <AuthImage
              path={item.passport_front}
              className="w-full h-40 object-cover rounded-xl border border-white/10 cursor-zoom-in"
              onClick={(e) => window.open(e.target.src, "_blank")}
            />
          )}
          {item.passport_back && (
            <AuthImage
              path={item.passport_back}
              className="w-full h-40 object-cover rounded-xl border border-white/10 cursor-zoom-in"
              onClick={(e) => window.open(e.target.src, "_blank")}
            />
          )}
          {item.document_photo && (
            <AuthImage
              path={item.document_photo}
              className="w-full h-40 object-cover rounded-xl border border-white/10 cursor-zoom-in"
              onClick={(e) => window.open(e.target.src, "_blank")}
            />
          )}
        </div>
        <button
          onClick={del}
          data-testid="detail-delete"
          className="w-full mt-5 flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 font-medium hover:bg-red-500/25 transition-colors"
        >
          <Trash2 className="w-4 h-4" /> Sil
        </button>
      </motion.div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-white/5 pb-2">
    <span className="text-slate-400">{label}</span>
    <span className="text-white text-right">{value}</span>
  </div>
);

export default function ClientsPortal() {
  const [authed, setAuthed] = useState(!!clientsToken());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: "", date_from: "", date_to: "", time_from: "", time_to: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const { data } = await clientsApi.get("/clients/purchases", { params });
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) {
        clientsLogout();
        setAuthed(false);
      } else {
        toast.error("Yüklənmədi");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  if (!authed) return <Gate onOk={() => setAuthed(true)} />;

  const clearFilters = () => {
    const empty = { q: "", date_from: "", date_to: "", time_from: "", time_to: "" };
    setFilters(empty);
    load(empty);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="clients-dashboard">
      <header className="sticky top-0 z-30 bg-[#0a0a0f]/90 backdrop-blur border-b border-white/10 px-4 sm:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#2997FF]" />
          <span className="font-semibold">Alış Jurnalı</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAdd(true)}
            data-testid="open-add-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#2997FF] text-white text-sm font-medium hover:bg-[#0a84ff] transition-colors"
          >
            <Plus className="w-4 h-4" /> Yeni alış
          </button>
          <button
            onClick={() => {
              clientsLogout();
              setAuthed(false);
            }}
            data-testid="clients-logout-btn"
            className="p-2 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:text-white"
            title="Çıxış"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4 mb-6">
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="IMEI, model, ad, soyad, nömrə, pasport..."
              data-testid="search-q"
              className="bg-white/5 border-white/10 h-11 pl-9 text-white"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Tarixdən</label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="search-date-from" className="bg-white/5 border-white/10 h-10 text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Tarixə</label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="search-date-to" className="bg-white/5 border-white/10 h-10 text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Saatdan</label>
              <Input type="text" inputMode="numeric" maxLength={5} placeholder="13:00" list="time-options" value={filters.time_from} onChange={(e) => setFilters((f) => ({ ...f, time_from: e.target.value }))} data-testid="search-time-from" className="bg-white/5 border-white/10 h-10 text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Saata</label>
              <Input type="text" inputMode="numeric" maxLength={5} placeholder="18:00" list="time-options" value={filters.time_to} onChange={(e) => setFilters((f) => ({ ...f, time_to: e.target.value }))} data-testid="search-time-to" className="bg-white/5 border-white/10 h-10 text-white" />
            </div>
          </div>
          <datalist id="time-options">
            {Array.from({ length: 48 }).map((_, i) => {
              const h = String(Math.floor(i / 2)).padStart(2, "0");
              const m = i % 2 ? "30" : "00";
              return <option key={i} value={`${h}:${m}`} />;
            })}
          </datalist>
          <div className="flex gap-2 mt-3">
            <button onClick={() => load()} data-testid="search-btn" className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-white text-black text-sm font-medium hover:bg-slate-200 transition-colors">
              <Search className="w-4 h-4" /> Axtar
            </button>
            <button onClick={clearFilters} data-testid="clear-btn" className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-slate-300 text-sm hover:text-white">
              Təmizlə
            </button>
          </div>
        </div>

        <div className="text-sm text-slate-400 mb-3" data-testid="results-count">
          {loading ? "Yüklənir..." : `${items.length} nəticə`}
        </div>

        <div className="space-y-2">
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => setDetail(it)}
              data-testid={`purchase-row-${it.id}`}
              className="w-full text-left rounded-xl bg-white/5 border border-white/10 p-4 hover:bg-white/10 transition-colors flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="font-medium truncate">
                  {it.seller_first_name} {it.seller_last_name} · <span className="text-[#2997FF]">{it.phone_model}</span>
                </div>
                <div className="text-xs text-slate-400 truncate">IMEI: {it.imei}{it.seller_phone ? ` · ${it.seller_phone}` : ""}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm">{it.purchase_date}</div>
                <div className="text-xs text-slate-400 flex items-center justify-end gap-1">
                  <Clock className="w-3 h-3" /> {it.purchase_at?.split("T")[1]?.slice(0, 5)}
                </div>
              </div>
            </button>
          ))}
          {!loading && items.length === 0 && (
            <div className="text-center text-slate-500 py-16" data-testid="no-results">Qeyd tapılmadı</div>
          )}
        </div>
      </div>

      {showAdd && (
        <AddModal
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            load();
          }}
        />
      )}
      {detail && (
        <DetailModal
          item={detail}
          onClose={() => setDetail(null)}
          onDeleted={() => {
            setDetail(null);
            load();
          }}
        />
      )}
    </div>
  );
}
